import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ErrorInfo, PostUsersTakeoverBody } from '../../../../api/model';
import { usePostUsersTakeover } from '../../../../api/users/users';
import { useDefaultOptions } from '../../../../hooks/api';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import Modal from '../../../modules/Modal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';

type Props = {
  open: boolean;
  onClose: () => void;
};

function UserTakeOverModal({ open, onClose }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  const [displayConfirmTakeover, setDisplayConfirmTakeover] = useState(false); // 確認ダイアログ

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PostUsersTakeoverBody>({
    defaultValues: {
      from_user_id: undefined,
      to_user_id: undefined,
    },
  });

  const { mutateAsync, error: takeOverError } = usePostUsersTakeover<AxiosError<ErrorInfo>, unknown>(options);
  const [displayTakeoverError, setdisplayTakeoverError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [displayTakeoverOk, setdisplayTakeoverOk] = useState(false);

  useEffect(() => {
    setdisplayTakeoverError(takeOverError != null);
    setIsSubmitting(false);
  }, [takeOverError]);

  const onSubmit = useCallback(() => {
    void handleSubmit(async (data: FieldValues) => {
      setIsSubmitting(true);
      await mutateAsync(
        {
          data,
        },
        {
          onSuccess: () => {
            setdisplayTakeoverOk(true);
          },
        }
      );
    })();
  }, [setIsSubmitting, setdisplayTakeoverOk, handleSubmit, mutateAsync]);

  return (
    <Modal active={open} additionalClassName="popupfr800" displayCloseButton onClose={onClose}>
      <form onSubmit={onSubmit}>
        <div className="popupfr pt30 popupfr644">
          <div>{t('ユーザー引継ぎ')}</div>
          <div className="teamsep2">
            <div>
              <label htmlFor="from_user_id">{t('引継ぎ元ユーザー')}</label>
              <input
                type="text"
                id="from_user_id"
                className={errors.from_user_id?.type === 'required' ? 'error' : ''}
                {...register('from_user_id', { required: true })}
              />
              {errors.from_user_id?.type === 'required' && (
                <p className="errormessage show">{t('引継ぎ元ユーザーは必須項目です')}</p>
              )}
            </div>
            <div>
              <label htmlFor="to_user_id">{t('引継ぎ先ユーザー')}</label>
              <input
                type="text"
                id="to_user_id"
                className={errors.to_user_id?.type === 'required' ? 'error' : ''}
                {...register('to_user_id', { required: true })}
              />

              {errors.to_user_id?.type === 'required' && (
                <p className="errormessage show">{t('引継ぎ先ユーザーは必須項目です')}</p>
              )}
            </div>
          </div>

          <div className="btnfr btnfr2 mb0">
            <div>
              <span className="btn2 btnbg-g mr10">
                <span className="modal-close2" onClick={onClose}>
                  {t('キャンセル').toString()}
                </span>
              </span>
              <span>
                <input
                  type="button"
                  value={t('更新').toString()}
                  className="btn1 lsp1"
                  onClick={() => setDisplayConfirmTakeover(true)}
                />
              </span>
            </div>
          </div>
        </div>
      </form>

      {/* ユーザー引継ぎ確認 */}
      {displayConfirmTakeover && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmTakeover(false)}
          onClickOk={() => {
            setDisplayConfirmTakeover(false);
            onSubmit();
          }}
        >
          {t('ユーザー引継ぎを行ないますか？')}
        </ConfirmModal>
      )}
      {isSubmitting && <SpinnerModal />}
      {displayTakeoverError && (
        <ErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setdisplayTakeoverError(false);
          }}
          error={takeOverError}
        />
      )}
      {displayTakeoverOk && (
        <OkModal
          onClickOk={() => {
            setIsSubmitting(false);
            setdisplayTakeoverOk(false);
            onClose();
          }}
        >
          <div>{t('引き継ぎ処理が完了しました。')}</div>
        </OkModal>
      )}
    </Modal>
  );
}

export { UserTakeOverModal };
